import { useShallow } from 'zustand/react/shallow';

import { useAppStore } from '@/modules/app/store/useAppStore';
import { assertNever } from '@/helpers/utils';

import { useFloorplanDesignStore } from '../store/useFloorplanDesignStore';
import { Panel_Display_Mode, PanelDisplayMode } from '../types';
import { areasToFlatCollection } from '../helpers';
import { templatesToFlatCollection } from '../helpers/helpers';

export const useReadSelectionHeaderState = (panelModeState: PanelDisplayMode) => {
  const selectedIds = useAppStore((state) => state.selectedIds);

  // TODO: Im really not a fan of this so if we finally have time 'next pi' we should give this some love...
  return useFloorplanDesignStore(
    useShallow((state) => {
      const selectedAreas = areasToFlatCollection(state.getAreasByIds(selectedIds));
      const selectedObstacles = state.getObstaclesByIds(selectedIds);
      const selectedTemplates = templatesToFlatCollection(state.getTemplatesByIds(selectedIds));
      const selectedLogicalSpaces = state.getLogicalSpacesByIds(selectedIds);
      const selectedGroups = state.getGroupsByIds(selectedIds);

      const selectedAreaCount = selectedAreas.length;
      const selectedObstacleCount = selectedObstacles.length;
      const selectedTemplateCount = selectedTemplates.length;
      const selectedLogicalSpaceCount = selectedLogicalSpaces.length;
      const selectedGroupCount = selectedGroups.length;

      const canDuplicateMulti =
        (panelModeState === Panel_Display_Mode.HIGHWAY ||
          panelModeState === Panel_Display_Mode.SINGULAR_STATION ||
          panelModeState === Panel_Display_Mode.STATIONARY ||
          panelModeState === Panel_Display_Mode.RACKING) &&
        selectedAreaCount === 1;

      const canTransformSelection =
        selectedGroupCount > 0 ||
        selectedAreaCount + selectedObstacleCount + selectedLogicalSpaceCount > 1;

      const showAlignPanel = selectedAreaCount + selectedObstacleCount + selectedTemplateCount > 1;

      let label = 'Unknown';
      if (
        selectedAreaCount !== 0 ||
        selectedObstacleCount !== 0 ||
        selectedTemplateCount !== 0 ||
        selectedLogicalSpaceCount !== 0 ||
        selectedGroupCount !== 0
      ) {
        switch (panelModeState) {
          case Panel_Display_Mode.MIX:
            label = `${
              selectedObstacleCount +
              selectedAreaCount +
              selectedLogicalSpaceCount +
              selectedGroupCount
            } Items`;
            break;
          case Panel_Display_Mode.WALL:
          case Panel_Display_Mode.OBSTACLE:
            label = selectedObstacles[0]?.name ?? 'Unknown';
            break;
          case Panel_Display_Mode.OBSTACLES:
            label = `${selectedObstacleCount} Obstacles`;
            break;
          case Panel_Display_Mode.GROUPS:
            label = `${selectedGroupCount} Groups`;
            break;
          case Panel_Display_Mode.GROUP:
            label = selectedGroups[0]?.name || selectedGroups[0]?.id || 'Unknown';
            break;
          case Panel_Display_Mode.TEMPLATE:
            label = `${selectedTemplateCount} Templates`;
            break;
          case Panel_Display_Mode.TEMPLATE_LASSIE:
          case Panel_Display_Mode.TEMPLATE_LASSIE_MINI:
          case Panel_Display_Mode.TEMPLATE_SHUTTLE_RACK:
          case Panel_Display_Mode.TEMPLATE_HIK:
            label =
              selectedTemplates[0]?.group?.name || selectedTemplates[0]?.group?.id || 'Unknown';
            break;
          case Panel_Display_Mode.AREA:
            label = `${selectedAreaCount} Areas`;
            break;
          case Panel_Display_Mode.HIGHWAY:
          case Panel_Display_Mode.SINGULAR_STATION:
          case Panel_Display_Mode.STATIONARY:
          case Panel_Display_Mode.RACKING:
          case Panel_Display_Mode.MANUAL:
            label = selectedAreas[0]?.name ?? 'Unknown';
            break;
          case Panel_Display_Mode.LOGICAL_SPACES:
            label = `${selectedLogicalSpaceCount} Logical Spaces`;
            break;
          case Panel_Display_Mode.LOGICAL_SPACE:
            label = selectedLogicalSpaces[0]?.name ?? 'Unknown';
            break;
          case Panel_Display_Mode.NONE:
            label = '';
            break;
          default:
            assertNever(panelModeState);
        }
      }

      return {
        label,
        showAlignPanel,
        canDuplicateMulti,
        canTransformSelection,
      };
    }),
  );
};
